/**
 * Copyright 2021 Olympe S.A.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { registerBrick } from 'olympe';
import { ReactBrick, useProperty, jsonToSxProps, cssToSxProps, ifNotTransparent, ifNotNull, useMUITheme } from '@olympeio/core';
import { ThemeProvider } from '@mui/material/styles';
import React, {useEffect} from 'react';
import IButton from '@mui/material/IconButton';
import Icon from "@mui/material/Icon";
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';


/**
 * Provide a Button visual component using MUI Button
 */
export default class IconButton extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return $.observe('Hidden');
    }

    /**
     * @override
     */
    updateParent(parent, element) {
        parent.style.overflow = 'visible'; // Allow overflow
        return super.updateParent(parent, element);
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            const [hidden] = props.values;
            const theme = useMUITheme($);

            const icon = useProperty($, 'icon', true);
            const style = useProperty($, 'icon style', true);
            const iconSize = useProperty($, 'icon size', true);
            const borderColor = useProperty($, 'Border Color', true);
            const borderRadius = useProperty($, 'Border Radius', true);
            const borderWidth = useProperty($, 'Border Width', true);
            const defaultColor = useProperty($, 'Default Color', true);
            const tooltip = useProperty($, 'tooltip', true);
            const tooltipPosition = useProperty($, 'tooltip position', true);

            const customSx = {
                ...ifNotTransparent('color', useProperty($, 'color', true)),
                ...ifNotTransparent('borderColor', borderColor),
                ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                ...ifNotNull('borderWidth', borderWidth),
                ...ifNotTransparent('borderStyle', 'solid', borderColor),
                ...ifNotTransparent('backgroundColor', defaultColor),
                '&:hover': {
                    ...ifNotTransparent('borderColor', borderColor),
                    ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                    ...ifNotNull('borderWidth', borderWidth),
                    ...ifNotTransparent('borderStyle', 'solid', borderColor),
                    ...ifNotTransparent('backgroundColor', defaultColor),
                },
            };

            useEffect(() => {
                IconButton.addIconsInPage(style?.toLowerCase());
            }, [style])


            const LightTooltip = styled(({ className, ...props }) => (
                <Tooltip {...props} classes={{ popper: className }} />
            ))(({ theme }) => ({
                [`& .${tooltipClasses.tooltip}`]: {
                    backgroundColor: theme.palette.secondary.dark,
                    color: 'white',
                    boxShadow: theme.shadows[1],
                    fontSize: 11,
                },
                [`& .${tooltipClasses.arrow}`]: {
                    "&:before": {
                        backgroundColor: theme.palette.secondary.dark,
                        boxShadow: theme.shadows[1],
                    }
                },
            }));

            return (
                !hidden && (
                    <ThemeProvider theme={theme}>
                        <LightTooltip title={tooltip} placement={tooltipPosition} arrow>
                            <IButton
                                disabled={useProperty($, 'disabled', true)}
                                tabIndex={useProperty($, 'Tab Index', true)}
                                onClick={() => $.trigger('On Click')}
                                sx={{
                                    width: 1,
                                    height: 1,
                                    ...customSx,
                                    boxSizing: 'border-box',
                                    borderRadius: '5px',
                                    ...cssToSxProps(useProperty($, 'CSS Property', true)),
                                    ...jsonToSxProps(useProperty($, 'mui sx', true)),
                                }}
                            >
                                <Icon baseClassName={'material-icons'+ (style === 'outlined' || style === 'two-tone' ? '-'+style : '')} sx={{fontSize: (iconSize ?? 24) + 'px !important'}}>{icon}</Icon>
                            </IButton>
                        </LightTooltip>
                    </ThemeProvider>
                )
            );
        };
    }

    static addIconsInPage (iconSet) {
        if(iconSet !== 'filled' && iconSet !== 'outlined' && iconSet !== 'two-tone') {
            return;
        }

        const createMaterialIconsElement = () => {
            let url = 'https://fonts.googleapis.com/icon?family=Material+Icons';
            switch (iconSet) {
                case 'outlined': url = 'https://fonts.googleapis.com/icon?family=Material+Icons+Outlined'; break;
                case 'two-tone': url = 'https://fonts.googleapis.com/icon?family=Material+Icons+Two+Tone'; break;
                default: break;
            }

            const materialIconsElement = document.createElement('link');
            materialIconsElement.id = 'materialIconsStyle'+iconSet;
            materialIconsElement.rel = 'stylesheet';
            materialIconsElement.type = 'text/css';
            materialIconsElement.href = url;
            return materialIconsElement;
        }

        const head = document.getElementsByTagName('head')[0];
        const hasMaterialIcon = document.getElementById('materialIconsStyle'+iconSet);
        if (!hasMaterialIcon) {
            const linkElement = createMaterialIconsElement();
            head.appendChild(linkElement);
        }
    }
}

registerBrick('019271e75aebb722aa0c', IconButton);
